import { DateTimeFormField } from "@/components/shared/forms/datetime.form-field";
import { ErrorModal } from "@/components/shared/modal/error-modal";
import Modal from "@/components/shared/modal/modal";
import { RootTypeEnum } from "@/models/roots/root-type.enum";
import { useComponentTypesContext } from "@/pages/configurations/componentTypes/context/ComponentTypeContext";
import { useRootTypeContext } from "@/pages/configurations/rootTypes/context/RootTypeContext";
import { useVendorsContext } from "@/pages/configurations/vendors/context/VendorsContext";
import { TundishComponents } from "@/pages/tundish-track/build-log/models/components";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Divider,
	FormControlLabel,
	Grid,
	MenuItem,
	Paper,
	Snackbar,
	Switch,
	TextField,
	Theme,
	Typography,
	createStyles,
	makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { usePartsContext } from "../../context/PartsContext";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { Part } from "../../models/Part";
import { createPart } from "../../repositories/PartsRepository";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		modalBody: {},
		title: {
			fontSize: 15,
		},
		checkbox: {
			userSelect: "none",
		},
		chip: {
			margin: 2,
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
		// The card behind these inputs is tinted, so they need their own background
		whiteInputs: {
			"& .MuiInputBase-root": {
				backgroundColor: "#fff",
			},
		},
		cancelButton: {
			color: theme.palette.error.dark,
			borderColor: theme.palette.error.dark,
		},
	}),
);

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const CreatePart = ({ show, onHide }: Props) => {
	const [open, setOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState("");
	const [error, setError] = useState<string | null>(null);
	const classes = useStyles();

	const {
		state: { parts },
	} = usePartsContext();
	const {
		state: { vendors },
	} = useVendorsContext();
	const {
		state: { componentTypes },
	} = useComponentTypesContext();
	const {
		state: { rootTypes },
	} = useRootTypeContext();

	// Asked for before anything else, and kept out of the form: a part has no root type
	// column, it only belongs to one through its component types. Mixing Ladle and
	// Tundish types on the same part is what this field prevents.
	const [rootTypeId, setRootTypeId] = useState<number | "">("");

	const sortedVendors = [...vendors].sort((a, b) =>
		a.vendorName.localeCompare(b.vendorName),
	);

	// Tracked types first, alphabetical within each group
	const sortedComponentTypes = [...componentTypes]
		.filter((ct) => ct.rootTypeId === rootTypeId)
		.sort(
			(a, b) =>
				Number(b.isTracked) - Number(a.isTracked) ||
				a.componentTypeName.localeCompare(b.componentTypeName),
		);

	const {
		register,
		control,
		reset,
		watch,
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<Part>({ defaultValues: INITIAL_VALUES });

	const selectedTypeIds = watch("componentTypeIds");
	const selectedTypes = componentTypes.filter((ct) =>
		selectedTypeIds.includes(ct.componentTypeId),
	);
	// Which parts keep their own refurbishment cycle in REF.PartRefurbishment, and so
	// have an arrival and a state of their own: the tracked ones (ComponentType.IsTracked,
	// the rule vSpecialComponent and vPartStatus filter on), minus the Permanent Lining.
	// A lining is relined on the tundish itself, so its cycle lives in REF.PLRefurbishment
	// keyed by TundishId; vSpecialComponent drops it at CandidateTasks, which only keeps
	// ComponentTypeId IN (312,313,314,315), and fPartMetric returns nothing for it.
	const tracksOwnRefurbishment =
		rootTypeId === RootTypeEnum.TUNDISH &&
		selectedTypes.some(
			(ct) =>
				ct.isTracked &&
				ct.componentTypeId !== TundishComponents.PermanentLining,
		);

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};

	const onSubmit: SubmitHandler<Part> = async (data) => {
		setIsSubmitLoading(true);
		try {
			await createPart(data);
			onHide(true);
			setMessage("The Part was created successfully");
			setMessageType("success");
		} catch (e) {
			setMessage("Error, please try again");
			setMessageType("error");
			setError(e as string);
		}
		setIsSubmitLoading(false);
		setOpen(true);
	};

	useEffect(() => {
		if (show) {
			reset(INITIAL_VALUES);
			setRootTypeId("");
		}
	}, [show]);

	// Left empty on purpose. An arrival is where fPartMetric starts counting
	// (vSpecialComponent keeps only the tasks with PerformTime > LastArrival), so
	// defaulting it to today hides every sequence the part ran before it was registered.
	// A part coming back from a vendor gets its arrival from the send-to-refurbish flow,
	// not from here. Clearing it when the field hides keeps a stale value from silently
	// creating a PartRefurbishment row the part has no use for.
	useEffect(() => {
		if (!tracksOwnRefurbishment) {
			setValue("actualArrival", null);
		}
	}, [tracksOwnRefurbishment]);

	return (
		<>
			<Modal
				open={show}
				handleClose={() => {
					onHide(true);
				}}
				id="createPart"
				title="Create a new part"
				maxWidth="md"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Modal.Content>
						{isLoading && (
							<Grid
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "300px" }}
							>
								{/* <LazyLoading /> */}
								<CircularProgress size="5rem" />
							</Grid>
						)}

						{!isLoading && (
							<>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<Typography variant="subtitle1">Part Type</Typography>
										<Divider />
									</Grid>
									<Grid item md={6} xs={12}>
										<TextField
											select
											label="Root Type"
											value={rootTypeId}
											onChange={(e) => {
												setRootTypeId(Number(e.target.value));
												// The types already picked belong to the previous root
												setValue("componentTypeIds", []);
											}}
											variant="outlined"
											fullWidth
											margin="dense"
										>
											{rootTypes.map((rt) => (
												<MenuItem value={rt.rootTypeId} key={rt.rootTypeId}>
													{rt.rootTypeName}
												</MenuItem>
											))}
										</TextField>
									</Grid>
									{/* Required: vPartStatus INNER JOINs PartComponentType, so a part
									    with no type here never shows up in the Build Log */}
									<Grid item md={6} xs={12}>
										<Controller
											name="componentTypeIds"
											control={control}
											rules={{ validate: { notEmpty: (v) => v.length > 0 } }}
											render={({ field }) => (
												<TextField
													select
													label="Component Types"
													value={field.value}
													onChange={field.onChange}
													variant="outlined"
													error={!!errors.componentTypeIds}
													disabled={rootTypeId === ""}
													helperText={
														rootTypeId === "" ? "Pick a root type first" : ""
													}
													fullWidth
													margin="dense"
													SelectProps={{
														multiple: true,
														renderValue: (selected) => (
															<div>
																{(selected as number[]).map((id) => (
																	<Chip
																		key={id}
																		className={classes.chip}
																		label={
																			componentTypes.find(
																				(ct) => ct.componentTypeId === id,
																			)?.componentTypeName
																		}
																	/>
																))}
															</div>
														),
													}}
												>
													{sortedComponentTypes.map((type) => (
														<MenuItem
															value={type.componentTypeId}
															key={type.componentTypeId}
														>
															{type.componentTypeName}
														</MenuItem>
													))}
												</TextField>
											)}
										/>
										{errors.componentTypeIds?.type === "notEmpty" && (
											<span className={classes.errorLabel}>
												At least one component type is required
											</span>
										)}
									</Grid>
									<Grid
										item
										xs={12}
										style={{ paddingTop: 0, paddingBottom: 0 }}
									>
										<Collapse
											in={tracksOwnRefurbishment}
											timeout="auto"
											unmountOnExit
										>
											<Paper
												variant="outlined"
												style={{
													backgroundColor: "#e3f2fd",
													borderLeft: "4px solid #1976d2",
													marginTop: 8,
													marginBottom: 8,
												}}
											>
												<Box p={2}>
													<Typography variant="subtitle2" gutterBottom>
														Tundish Special Component
													</Typography>
													<Divider style={{ marginBottom: 16 }} />
													<Grid
														container
														spacing={2}
														className={classes.whiteInputs}
													>
														<Grid item md={6} xs={12}>
															<Controller
																name="actualArrival"
																control={control}
																render={({ field: { value, onChange } }) => (
																	<DateTimeFormField
																		variant="outlined"
																		title="Arrival Date"
																		value={value}
																		onChange={onChange}
																	/>
																)}
															/>
														</Grid>
														<Grid item md={6} xs={12}>
															<TextField
																label="Part State"
																value="Available"
																disabled
																variant="outlined"
																fullWidth
															/>
														</Grid>
													</Grid>
												</Box>
											</Paper>
										</Collapse>
									</Grid>
									<Grid item xs={12}>
										<Typography variant="subtitle1">Part Details</Typography>
										<Divider />
									</Grid>
									<Grid item md={6} xs={12}>
										<Controller
											name="partName"
											rules={{
												required: true,
												validate: { notBlank: (v) => `${v}`.trim().length > 0 },
											}}
											control={control}
											render={({ field }) => (
												<TextField
													label="Part Name"
													value={field.value}
													onChange={field.onChange}
													variant="outlined"
													error={!!errors.partName}
													fullWidth
													margin="dense"
												/>
											)}
										/>

										{errors.partName && (
											<span className={classes.errorLabel}>
												Part Name is required
											</span>
										)}
									</Grid>
									<Grid item md={6} xs={12}>
										<Controller
											name="vendorId"
											control={control}
											rules={{
												required: true,
												validate: { positive: (v) => v != 0 },
											}}
											render={({ field }) => (
												<TextField
													select
													label="Vendor"
													value={field.value}
													onChange={field.onChange}
													variant="outlined"
													error={!!errors.vendorId}
													fullWidth
													margin="dense"
												>
													{sortedVendors.map((type) => (
														<MenuItem value={type.vendorId} key={type.vendorId}>
															{type.vendorName}
														</MenuItem>
													))}
												</TextField>
											)}
										/>
										{errors.vendorId?.type === "positive" && (
											<span className={classes.errorLabel}>
												This field is required
											</span>
										)}
									</Grid>
									<Grid item md={12} xs={12}>
										<Controller
											name="description"
											rules={{
												required: true,
												validate: { notBlank: (v) => `${v}`.trim().length > 0 },
											}}
											control={control}
											render={({ field }) => (
												<TextField
													label="Description"
													value={field.value}
													onChange={field.onChange}
													variant="outlined"
													error={!!errors.description}
													fullWidth
													margin="dense"
													multiline
													minRows={3}
												/>
											)}
										/>

										{errors.description && (
											<span className={classes.errorLabel}>
												Description is required
											</span>
										)}
									</Grid>
									<Grid item md={6} xs={12}>
										<Controller
											name="sku"
											rules={{
												required: true,
												validate: { notBlank: (v) => `${v}`.trim().length > 0 },
											}}
											control={control}
											render={({ field }) => (
												<TextField
													label="SKU"
													value={field.value}
													onChange={field.onChange}
													variant="outlined"
													error={!!errors.sku}
													fullWidth
													margin="dense"
												/>
											)}
										/>

										{errors.sku && (
											<span className={classes.errorLabel}>
												SKU is required
											</span>
										)}
									</Grid>
									<Grid item md={6} xs={12}>
										<Controller
											name="unit"
											rules={{
												required: true,
												validate: { notBlank: (v) => `${v}`.trim().length > 0 },
											}}
											control={control}
											render={({ field }) => (
												<TextField
													label="Unit"
													value={field.value}
													onChange={field.onChange}
													variant="outlined"
													error={!!errors.unit}
													fullWidth
													margin="dense"
												/>
											)}
										/>

										{errors.unit && (
											<span className={classes.errorLabel}>
												Unit is required
											</span>
										)}
									</Grid>
									<Grid item xs={12}>
										<Controller
											name="isActive"
											control={control}
											render={({ field }) => (
												<FormControlLabel
													control={
														<Switch
															color="primary"
															checked={field.value}
															onChange={(e) => field.onChange(e.target.checked)}
														/>
													}
													label={`Status ${
														field.value ? "Active" : "Inactive"
													}`}
												/>
											)}
										/>
									</Grid>
								</Grid>
							</>
						)}
					</Modal.Content>
					<Modal.Actions style={{ padding: "1rem" }}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</Button>
							</Grid>
							<Grid item xs={12}>
								<Button
									fullWidth
									variant="outlined"
									className={classes.cancelButton}
									onClick={() => {
										onHide(false);
									}}
								>
									Cancel
								</Button>
							</Grid>
						</Grid>
					</Modal.Actions>
				</form>
			</Modal>

			<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
				{messageType == "success" ? (
					<Alert severity="success" onClose={handleClose}>
						{message}
					</Alert>
				) : (
					<Alert severity="error" onClose={handleClose}>
						{message}
					</Alert>
				)}
			</Snackbar>

			<ErrorModal onHide={() => setError(null)} error={error} />
		</>
	);
};
